import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import React from 'react';

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
var _richTextReactRenderer = require("@contentful/rich-text-react-renderer");
var _contentfulResolveResponse = _interopRequireDefault(require("contentful-resolve-response"));

import RichAsset from '../rich-components/rich-asset';
import RichCode from '../rich-components/rich-code';
import RichEntry from '../rich-components/rich-entry';
import RichHeading from '../rich-components/rich-heading';
import RichHyperlink from '../rich-components/rich-hyperlink';
import RichListItem from '../rich-components/rich-list-item';
import RichOrderedList from '../rich-components/rich-ordered-list';
import RichUnorderedList from '../rich-components/rich-unordered-list';
import RichParagraph from '../rich-components/rich-paragraph';
import RichQuote from '../rich-components/rich-quote';

/**
 * Function which accepts a rich text JSON object and converts it into styled
 * react components using a combination of custom components, and standard elements
 * configured for rich-text-react-renderer
 *
 * @function renderRichText
 * @returns {jsx} The JSX comprised of a collection of rendered components
 */
const renderRichText = (richJSON, pageGeo, fontSize = 'large') => {
  if (!richJSON) {
    return null;
  }

  const { raw, references } = richJSON;
  const richText = JSON.parse(raw);
  let referenceCounter = 0;

  const options = {
    renderMark: {
      [MARKS.CODE]: RichCode,
      subscript: text => <span style={{ verticalAlign: 'sub' }}>{text}</span>,
      superscript: text => <span style={{ verticalAlign: 'super' }}>{text}</span>
    },
    renderNode: {
      [BLOCKS.HEADING_1]: RichHeading,
      [BLOCKS.HEADING_2]: RichHeading,
      [BLOCKS.HEADING_3]: RichHeading,
      [BLOCKS.HEADING_4]: RichHeading,
      [BLOCKS.HEADING_5]: RichHeading,
      [BLOCKS.HEADING_6]: RichHeading,
      [BLOCKS.LIST_ITEM]: RichListItem,
      [BLOCKS.UL_LIST]: RichUnorderedList,
      [BLOCKS.OL_LIST]: RichOrderedList,
      [BLOCKS.QUOTE]: RichQuote,
      [BLOCKS.PARAGRAPH]: (node, children) => {
        return RichParagraph(node, children, fontSize);
      },
      [INLINES.HYPERLINK]: (node, children) => {
        return RichHyperlink(pageGeo, node, children);
      },
      [BLOCKS.EMBEDDED_ASSET]: () => {
        const richAssetContent = references && RichAsset(references[referenceCounter]);
        referenceCounter++;
        return richAssetContent;
      },
      [BLOCKS.EMBEDDED_ENTRY]: () => {
        const richEntryContent = references && RichEntry(references[referenceCounter], pageGeo);
        referenceCounter++;
        return richEntryContent;
      }
    }
  };

  if (!references || !references.length) {
    return _richTextReactRenderer.documentToReactComponents(richText, options);
  }

  const dummyResponse = {
    items: [
      {
        sys: {
          type: `Entry`
        },
        richText
      }
    ],
    includes: {
      Entry: references
        .filter(({ internal }) => internal?.type !== `ContentfulAsset`)
        .map(reference => ({
          ...reference,
          sys: {
            type: `Entry`,
            id: reference.id
          }
        })),
      Asset: references
        .filter(({ internal }) => internal?.type === `ContentfulAsset`)
        .map(reference => ({
          ...reference,
          sys: {
            type: `Asset`,
            id: reference.id
          }
        }))
    }
  };

  const resolved = _contentfulResolveResponse.default(dummyResponse, {
    removeUnresolved: true
  });

  return _richTextReactRenderer.documentToReactComponents(resolved[0].richText, options);
};

export default renderRichText
